import React, { useContext } from 'react';
import { mapContext } from './mapContext';
import { Icon, LatLng } from 'leaflet';
import { GeoJSON, Marker, Polyline } from 'react-leaflet';
import { stopIcon } from '../../../components/Map/ReducedMap/Icons';
import { theme } from '../../../../theme';
import { DetailsInfoType } from './MapPage.types';
import { parsePoint } from '../../../../utils/map';
import agencyIcon from '../../../../images/point_2.svg';
import depotSIcon from '../../../../images/point_1.svg';

export const MapObjects = () => {
  const {
    mapRef,
    mapElements,
    mapSettings: { mapSettings },
    filteredRoutes,
    agenciesFilters,
    selectedAgencies,
    layers,
    setDetailsInfo,
    BCRegions,
  } = useContext(mapContext);

  const checkIncluding = (stop: { position: LatLng }) =>
    mapRef?.current?.leafletElement.getBounds().contains(stop.position);

  const filterByRoute = (route_id: number) =>
    !filteredRoutes.length || filteredRoutes.find((r) => r === route_id);

  let stops = mapElements?.selectedStops
    .filter(({ route_id }) => filterByRoute(route_id))
    .filter(checkIncluding)
    .map(({ id, position }, index) => (
      <Marker
        onClick={() =>
          setDetailsInfo({
            type: DetailsInfoType.stop,
            id,
          })
        }
        icon={stopIcon()}
        position={position}
        key={`${id} ${index}`}
      />
    ));

  if (mapSettings.zoom <= 12) {
    stops = mapElements?.minimizedStops
      .filter(({ route_id }) => filterByRoute(route_id))
      .filter(checkIncluding)
      .map(({ id, position }, index) => (
        <Marker
          onClick={() =>
            setDetailsInfo({
              type: DetailsInfoType.stop,
              id,
            })
          }
          icon={stopIcon()}
          position={position}
          key={`${id} ${index}`}
        />
      ));
  }

  const routes = mapElements?.selectedRoutes
    .filter(({ id }) => filterByRoute(id))
    .map(({ position, color, id }, index) => (
      <Polyline
        positions={position}
        key={`${id} ${index}`}
        color={color || theme.colors.lightBlue}
        weight={3}
        onClick={() =>
          setDetailsInfo({
            type: DetailsInfoType.route,
            id,
          })
        }
      />
    ));

  const selectedDepots = agenciesFilters.depots
    .filter(({ agency_id }) => selectedAgencies.includes(agency_id))
    .map(({ depot_id }) => depot_id);

  const mapAgencies = agenciesFilters.agencies
    .filter(
      ({ agency_id, ag_lat, ag_lon }) =>
        ag_lon &&
        ag_lat &&
        (!selectedAgencies.length || selectedAgencies.includes(agency_id))
    )
    .map(({ agency_id, ag_lat, ag_lon }) => (
      <Marker
        onClick={() =>
          setDetailsInfo({
            type: DetailsInfoType.agency,
            id: agency_id,
          })
        }
        position={[ag_lat, ag_lon]}
        key={agency_id}
        icon={
          new Icon({
            iconUrl: agencyIcon,
            iconSize: [24, 24],
          })
        }
      />
    ));

  const mapDepots = agenciesFilters.depots
    .filter(
      ({ depot_id, dp_lat, dp_lon }) =>
        dp_lat &&
        dp_lon &&
        (!selectedDepots.length || selectedDepots.includes(depot_id))
    )
    .map(({ depot_id, dp_lat, dp_lon }) => (
      <Marker
        onClick={() =>
          setDetailsInfo({
            id: depot_id,
            type: DetailsInfoType.depot,
          })
        }
        key={depot_id}
        position={[dp_lat, dp_lon]}
        icon={
          new Icon({
            iconUrl: depotSIcon,
            iconSize: [24, 24],
          })
        }
      />
    ));

  const mapDepartments = agenciesFilters.departments
    .filter(
      ({ depot_id, td_lat, td_lon }) =>
        td_lat &&
        td_lon &&
        (!selectedDepots.length || selectedDepots.includes(depot_id))
    )
    .map(({ td_id, td_lat, td_lon }) => (
      <Marker
        onClick={() =>
          setDetailsInfo({
            id: td_id,
            type: DetailsInfoType.department,
          })
        }
        key={td_id}
        position={[td_lat, td_lon]}
        icon={stopIcon(true)}
      />
    ));

  const bcRegions = BCRegions.map(({ geojson, td_color, td_id }) => (
    <GeoJSON
      id={'REGION_' + td_id}
      onClick={() =>
        setDetailsInfo({
          type: DetailsInfoType.bcregions,
          id: td_id,
        })
      }
      data={JSON.parse(geojson)}
      color={td_color}
      key={td_id}
    />
  ));

  const bcDeps = BCRegions.map(({ center, td_id }) => {
    const position = parsePoint(center);
    if (!position) return null;
    return (
      <Marker
        key={td_id}
        icon={stopIcon('#00C8FF', '#003a72')}
        position={position}
        onClick={() =>
          setDetailsInfo({
            type: DetailsInfoType.department,
            id: td_id,
          })
        }
      />
    );
  });

  return (
    <>
      {layers.ROUTES.active && routes}
      {layers.STOPS.active && stops}
      {layers.AGENCIES?.active && mapDepots}
      {layers.DEPARTMENTS?.active && mapAgencies}
      {layers.BCREGIONS?.active && bcRegions}
      {layers.BC_DEP?.active && bcDeps}
    </>
  );
};
